import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, CircularProgress,
  Alert, MenuItem, IconButton, Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  LockReset as ResetIcon,
  Delete as DeleteIcon,
  ToggleOn as ActivateIcon,
  ToggleOff as DeactivateIcon
} from '@mui/icons-material';
import { userAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tempPassword, setTempPassword] = useState('');

  // Action modal state
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionModal, setActionModal] = useState(null); // 'reset-password' | 'delete' | 'toggle-status'
  const [newPassword, setNewPassword] = useState('');
  const [generateRandom, setGenerateRandom] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');

  const { isAdmin, isManager, user: currentUser } = useAuth();

  const [formData, setFormData] = useState({
    name: '', email: '', password: '', phone: '', role: 'supervisor'
  });

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getAll();
      let filteredUsers = response.data.data.users;

      // Hide deleted users
      filteredUsers = filteredUsers.filter(u => u.status !== 'deleted');

      // Filter by role
      if (isManager && !isAdmin) {
        // Managers only see supervisors
        filteredUsers = filteredUsers.filter(u => u.role === 'supervisor');
      }

      setUsers(filteredUsers);
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setTempPassword('');
    try {
      const userData = { ...formData };
      if (!userData.password || userData.password.trim() === '') delete userData.password;

      const response = await userAPI.create(userData);
      if (response.data.data.temporaryPassword) {
        setTempPassword(response.data.data.temporaryPassword);
        setSuccess(`User created successfully! Temporary password: ${response.data.data.temporaryPassword}`);
      } else {
        setSuccess('User created successfully! Welcome email sent with login credentials.');
      }
      setOpen(false);
      fetchUsers();
      setFormData({ name: '', email: '', password: '', phone: '', role: 'supervisor' });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create user');
    }
  };

  const openActionModal = (user, action) => {
    setSelectedUser(user);
    setActionModal(action);
    setActionError('');
    setActionSuccess('');
    setNewPassword('');
    setGenerateRandom(true);
    setGeneratedPassword('');
  };

  const closeActionModal = () => {
    setActionModal(null);
    setSelectedUser(null);
    setActionError('');
    setActionSuccess('');
    setNewPassword('');
    setGeneratedPassword('');
  };

  const handleToggleStatus = async () => {
    setActionLoading(true);
    setActionError('');
    try {
      await api.put(`/users/${selectedUser.id}/toggle-status`);
      const newStatus = selectedUser.status === 'active' ? 'inactive' : 'active';
      setActionSuccess(`User ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully!`);
      fetchUsers();
      setTimeout(() => closeActionModal(), 1500);
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    setActionLoading(true);
    setActionError('');
    try {
      await api.delete(`/users/${selectedUser.id}`);
      setActionSuccess('User deleted successfully!');
      fetchUsers();
      setTimeout(() => closeActionModal(), 1500);
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to delete user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setActionLoading(true);
    setActionError('');
    try {
      const body = generateRandom ? {} : { newPassword };
      const response = await api.put(`/users/${selectedUser.id}/reset-password`, body);
      const pwd = response.data.data?.temporaryPassword;
      setGeneratedPassword(pwd || '');
      setActionSuccess(
        pwd
          ? `Password reset! New password: ${pwd} (also emailed to user)`
          : 'Password updated successfully! User has been notified via email.'
      );
      fetchUsers();
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setActionLoading(false);
    }
  };

  // Permission checks
  const canToggleStatus = (targetUser) => {
    if (targetUser.id === currentUser?.id) return false;
    if (targetUser.status === 'deleted') return false;
    if (isAdmin) return true;
    if (isManager && targetUser.role !== 'admin') return true;
    return false;
  };

  const canResetPassword = (targetUser) => {
    if (targetUser.id === currentUser?.id) return false;
    if (targetUser.status === 'deleted') return false;
    if (isAdmin) return true;
    if (isManager && targetUser.role !== 'admin') return true;
    return false;
  };

  const canDelete = (targetUser) => {
    if (!isAdmin) return false;
    if (targetUser.id === currentUser?.id) return false;
    if (targetUser.role === 'admin') return false;
    if (targetUser.status === 'deleted') return false;
    return true;
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'error';
      case 'manager': return 'warning';
      case 'supervisor': return 'info';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'deleted': return 'error';
      default: return 'default';
    }
  };

  if (!isAdmin && !isManager) {
    return <Alert severity="error">You don't have permission to access this page.</Alert>;
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Users Management</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
          Create User
        </Button>
      </Box>

      {/* Alerts */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
          {tempPassword && (
            <Box sx={{ mt: 1, p: 1, bgcolor: 'warning.light', borderRadius: 1 }}>
              <Typography variant="body2" fontWeight="bold">⚠️ Save this password - it won't be shown again!</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5 }}>Temporary Password: {tempPassword}</Typography>
            </Box>
          )}
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Users Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} align="center"><CircularProgress /></TableCell></TableRow>
            ) : users.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center">No users found</TableCell></TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id} sx={{ opacity: user.status === 'inactive' ? 0.65 : 1, bgcolor: user.status === 'deleted' ? '#FEF2F2' : 'inherit' }}>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.phone || '-'}</TableCell>
                  <TableCell><Chip label={user.role} color={getRoleColor(user.role)} size="small" /></TableCell>
                  <TableCell><Chip label={user.status} color={getStatusColor(user.status)} size="small" /></TableCell>
                  <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>

                      {/* Toggle Active/Inactive */}
                      {canToggleStatus(user) && (
                        <Tooltip title={user.status === 'active' ? 'Deactivate User' : 'Activate User'}>
                          <IconButton size="small" color={user.status === 'active' ? 'warning' : 'success'}
                            onClick={() => openActionModal(user, 'toggle-status')}>
                            {user.status === 'active' ? <DeactivateIcon /> : <ActivateIcon />}
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Reset Password */}
                      {canResetPassword(user) && (
                        <Tooltip title="Reset Password">
                          <IconButton size="small" color="primary"
                            onClick={() => openActionModal(user, 'reset-password')}>
                            <ResetIcon />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Delete (Admin only) */}
                      {canDelete(user) && (
                        <Tooltip title="Delete User">
                          <IconButton size="small" color="error"
                            onClick={() => openActionModal(user, 'delete')}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}

                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ===== TOGGLE STATUS MODAL ===== */}
      <Dialog open={actionModal === 'toggle-status'} onClose={closeActionModal} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: selectedUser?.status === 'active' ? 'warning.main' : 'success.main', color: 'white' }}>
          {selectedUser?.status === 'active' ? '⚠️ Deactivate User' : '✅ Activate User'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}
          {actionSuccess && <Alert severity="success" sx={{ mb: 2 }}>{actionSuccess}</Alert>}
          {selectedUser && !actionSuccess && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: '#F9FAFB', borderRadius: 2, mb: 2 }}>
                <Box sx={{ width: 44, height: 44, bgcolor: '#FFF7ED', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ color: '#e59225', fontWeight: 700 }}>{selectedUser.name?.charAt(0)?.toUpperCase()}</Typography>
                </Box>
                <Box>
                  <Typography fontWeight={600}>{selectedUser.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{selectedUser.email}</Typography>
                  <Chip label={selectedUser.role} color={getRoleColor(selectedUser.role)} size="small" sx={{ mt: 0.5 }} />
                </Box>
              </Box>
              <Alert severity={selectedUser.status === 'active' ? 'warning' : 'info'} sx={{ fontSize: 13 }}>
                {selectedUser.status === 'active'
                  ? '⚠️ Deactivating will immediately log this user out and block future logins until reactivated.'
                  : '✅ Activating will allow this user to login again.'
                }
              </Alert>
            </>
          )}
        </DialogContent>
        {!actionSuccess && (
          <DialogActions>
            <Button onClick={closeActionModal} disabled={actionLoading}>Cancel</Button>
            <Button onClick={handleToggleStatus} variant="contained"
              color={selectedUser?.status === 'active' ? 'warning' : 'success'}
              disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={20} /> : selectedUser?.status === 'active' ? 'Deactivate' : 'Activate'}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* ===== RESET PASSWORD MODAL ===== */}
      <Dialog open={actionModal === 'reset-password'} onClose={closeActionModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#3B82F6', color: 'white' }}>
          🔑 Reset Password
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}
          {actionSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {actionSuccess}
              {generatedPassword && (
                <Box sx={{ mt: 1, p: 1.5, bgcolor: 'warning.light', borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight="bold">⚠️ Copy this now - shown only once!</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 16, mt: 0.5, letterSpacing: 2 }}>
                    {generatedPassword}
                  </Typography>
                </Box>
              )}
            </Alert>
          )}
          {selectedUser && !actionSuccess && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: '#F9FAFB', borderRadius: 2, mb: 3 }}>
                <Box sx={{ width: 44, height: 44, bgcolor: '#EFF6FF', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ color: '#3B82F6', fontWeight: 700 }}>{selectedUser.name?.charAt(0)?.toUpperCase()}</Typography>
                </Box>
                <Box>
                  <Typography fontWeight={600}>{selectedUser.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{selectedUser.email}</Typography>
                </Box>
              </Box>

              {/* Password Type Toggle */}
              <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                <Button fullWidth variant={generateRandom ? 'contained' : 'outlined'}
                  onClick={() => setGenerateRandom(true)} sx={{ py: 1.5 }}>
                  🎲 Generate Random
                </Button>
                <Button fullWidth variant={!generateRandom ? 'contained' : 'outlined'}
                  onClick={() => setGenerateRandom(false)} sx={{ py: 1.5 }}>
                  ✏️ Set Manually
                </Button>
              </Box>

              {generateRandom ? (
                <Alert severity="info">
                  A random secure password will be generated and emailed to <strong>{selectedUser.email}</strong>. It will also be shown to you after reset.
                </Alert>
              ) : (
                <TextField fullWidth type="password" label="New Password"
                  value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  helperText="Minimum 6 characters. User will be notified via email."
                  inputProps={{ autoComplete: 'new-password' }} />
              )}
            </>
          )}
        </DialogContent>
        {!actionSuccess && (
          <DialogActions>
            <Button onClick={closeActionModal} disabled={actionLoading}>Cancel</Button>
            <Button onClick={handleResetPassword} variant="contained" color="primary"
              disabled={actionLoading || (!generateRandom && newPassword.length < 6)}>
              {actionLoading ? <CircularProgress size={20} /> : 'Reset Password'}
            </Button>
          </DialogActions>
        )}
        {actionSuccess && (
          <DialogActions>
            <Button onClick={closeActionModal} variant="contained">Done</Button>
          </DialogActions>
        )}
      </Dialog>

      {/* ===== DELETE USER MODAL ===== */}
      <Dialog open={actionModal === 'delete'} onClose={closeActionModal} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: 'error.main', color: 'white' }}>
          🗑️ Delete User
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}
          {actionSuccess && <Alert severity="success" sx={{ mb: 2 }}>{actionSuccess}</Alert>}
          {selectedUser && !actionSuccess && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: '#FEF2F2', borderRadius: 2, mb: 2 }}>
                <Box sx={{ width: 44, height: 44, bgcolor: '#FEE2E2', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ color: '#EF4444', fontWeight: 700 }}>{selectedUser.name?.charAt(0)?.toUpperCase()}</Typography>
                </Box>
                <Box>
                  <Typography fontWeight={600}>{selectedUser.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{selectedUser.email}</Typography>
                  <Chip label={selectedUser.role} color={getRoleColor(selectedUser.role)} size="small" sx={{ mt: 0.5 }} />
                </Box>
              </Box>
              <Alert severity="warning">
                <Typography variant="body2" fontWeight={600} gutterBottom>This is a soft delete:</Typography>
                <Typography variant="body2">• User will be immediately logged out</Typography>
                <Typography variant="body2">• They cannot login again</Typography>
                <Typography variant="body2">• Their data and history is preserved</Typography>
              </Alert>
            </>
          )}
        </DialogContent>
        {!actionSuccess && (
          <DialogActions>
            <Button onClick={closeActionModal} disabled={actionLoading}>Cancel</Button>
            <Button onClick={handleDeleteUser} variant="contained" color="error" disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={20} /> : 'Delete User'}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* ===== CREATE USER DIALOG ===== */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>Create New User</DialogTitle>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField fullWidth label="Full Name" name="name" value={formData.name} onChange={handleChange} required />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth type="email" label="Email" name="email" value={formData.email} onChange={handleChange} required />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth type="password" label="Password (optional)" name="password" value={formData.password}
                  onChange={handleChange} helperText="Leave empty to auto-generate a temporary password. User will receive it via email."
                  autoComplete="new-password" inputProps={{ autoComplete: 'new-password' }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Phone" name="phone" value={formData.phone} onChange={handleChange} />
              </Grid>
              <Grid item xs={12}>
                <TextField select fullWidth label="Role" name="role" value={formData.role} onChange={handleChange} required>
                  {isAdmin && <MenuItem value="admin">Admin</MenuItem>}
                  {isAdmin && <MenuItem value="manager">Manager</MenuItem>}
                  {isAdmin && <MenuItem value="vendor_manager">Vendor Manager</MenuItem>}
                  <MenuItem value="supervisor">Supervisor</MenuItem>
                </TextField>
                {isManager && <Typography variant="caption" color="text.secondary">Managers can only create supervisors</Typography>}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained">Create</Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default UsersPage;